import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useMutation } from '@apollo/client';
import Modal from '../../../../../../components/Modal';
import TextInput from '../../../../../../components/TextInput';
import { formatPrice } from '@/utils/formatter';
import { uploadToS3 } from '@/utils/imageUpload';
import {
  updateProduct,
  requestUploadUrl,
  updateProductAsCopy as updateProductAsCopyMutation,
} from '../../../../../../graphql/mutations';
import { Sheet } from '../AllergenModal/styled';
import {
  Field,
  ProductImage,
  SaveCopyButton,
  Select,
  CategoryLinkContainer,
  CategoryStoreLink,
  ProductButtonContainer,
} from './styled';
import Button from '../../../../../../components/Button';
import { HeaderSmall } from '../../../../../../components/Text';

import {
  brandCategoryProductsByStore as brandCategoryProductsByStoreQuery,
  productCategories as productCategoriesQuery,
  productCategories as productCategoriesMutation,
} from '../../../../../../graphql/queries';
import { replaceProductByIdInApolloStore } from '../../../../../../graphql/storeUpdater';
import Draggable from '../../../../../../components/Draggable';
import { changeOrderHelper, getProductInput } from '@/utils/data';
import ExtraGroupFormSection from '../../../../../../components/ExtraGroupFormSection';
import { categoryPlaceholderList } from '../../../CategoryEditModal/placeholderList';
import { PlaceholderSelectionImg } from '../../../CategoryEditModal/style';
import VatSelector from '../../../../../../components/VatSelector';

import Checkbox from '../../../../../../components/Checkbox';
import { randomId } from '@/utils/random';
import {
  CheckBoxHelpText,
  CheckBoxLabel,
  CheckBoxLabelContainer,
} from '../../../../../../components/Checkbox/pickpackCheckbox';
import i18n from '@/utils/i18n';

function ProductEditModal({
  storeId,
  category,
  product,
  onClose,
  productCategoryMap,
  vatRates,
}) {
  const [values, setValues] = useState(product);
  const [updateProductMutation, { loading: updating }] =
    useMutation(updateProduct);
  const [updateProductMutationRemoveImage, { loading: removingProductImage }] =
    useMutation(updateProduct);
  const [requestUploadUrlQuery] = useMutation(requestUploadUrl);
  const [updateProductAsCopy] = useMutation(updateProductAsCopyMutation);
  const touchedValues = useMemo(
    () =>
      Object.keys(values).reduce((touched, key) => {
        if (product[key] !== values[key])
          return { ...touched, [key]: values[key] };
        return touched;
      }, {}),
    [product, values],
  );

  function handleChange({ target: { type, name, value } }) {
    let formattedValue = value;
    if (type === 'number' || type === 'select-one' || name === 'vat_rate') {
      formattedValue = +value;
    }
    if (name === 'has_cutlery') {
      formattedValue = value === 'null' ? null : value === 'true';
    }
    setValues({ ...values, [name]: formattedValue });
  }
  function handleChangeManually(newVal, name) {
    setValues({ ...values, [name]: newVal });
  }

  function handleSave() {
    if (Object.keys(touchedValues).length) {
      updateProductMutation({
        variables: { id: product.id, product: getProductInput(touchedValues) },
        refetchQueries: [
          {
            query: productCategoriesQuery,
            variables: {
              id: storeId,
              filter: 'ALL',
            },
          },
        ],
        update: () => {
          onClose();
        },
      });
    }
  }

  function handleSaveAsCopy() {
    if (Object.keys(touchedValues).length > 0) {
      updateProductAsCopy({
        variables: {
          id: product.id,
          product: getProductInput(touchedValues),
          replace_in_category: category.id,
        },
        refetchQueries: [
          {
            query: brandCategoryProductsByStoreQuery,
            variables: {
              store_id: storeId,
            },
          },
          {
            query: productCategoriesMutation,
            variables: {
              id: storeId,
              filter: 'ALL',
            },
          },
        ],
        update: (apolloStore, mutationResult) => {
          /*
          if (mutationResult.data.updateProductAsCopy) {
            const copiedProductId = mutationResult.data.updateProductAsCopy.id
            const updatedProduct = {
              ...product,
              ...touchedValues,
              id: copiedProductId,
            }
            replaceProductByIdInApolloStore(apolloStore, storeId, product.id, updatedProduct, category.id)
          }*/
          onClose();
        },
      });
    } else {
      onClose();
    }
  }

  async function onUploadImage(e) {
    const { files } = e.target;
    const file = (files || [])[0];
    if (file) {
      const { name: filename, type: filetype } = file;
      let uniqueFilename = randomId(6) + '_' + filename;
      const { data } = await requestUploadUrlQuery({
        variables: { filename: uniqueFilename, filetype },
      });
      if (data.requestUploadUrl) {
        const url = await uploadToS3(file, data.requestUploadUrl);
        if (url !== product.image) {
          setValues({ ...values, image: url });
        }
      }
    }
  }

  const usedInCategoriesMap = productCategoryMap[product.id];

  let inCategoriesUsedCount = 0;
  let usedMultipleTimes = false;
  let storeCatLinks = [];

  if (usedInCategoriesMap) {
    inCategoriesUsedCount = Object.keys(usedInCategoriesMap).length;
    usedMultipleTimes = inCategoriesUsedCount > 1;
    storeCatLinks = Object.keys(usedInCategoriesMap).map((catKey) => (
      <span key={catKey}>
        <CategoryStoreLink
          href={`/de/store/${usedInCategoriesMap[catKey]}#${encodeURIComponent(
            catKey,
          )}`}
        >
          {catKey}
        </CategoryStoreLink>{' '}
      </span>
    ));
  }

  function removeImage() {
    const res = window.confirm(i18n.t('Menu:removeImageWarn'));
    if (!res) {
      return;
    }

    updateProductMutationRemoveImage({
      variables: {
        id: product.id,
        product: {
          image: null,
        },
      },
      update: (_, args) => {
        console.log(args);
        onClose();
      },
    });
  }

  function sortVariants(variantId, targetVariantId) {
    setValues({
      ...values,
      variants: changeOrderHelper(
        values.variants,
        'variant_id',
        variantId,
        targetVariantId,
      ),
    });
  }

  function onDragStartVariant(event) {
    event.dataTransfer.setData('text/plain', event.target.id);
  }

  function onDragOverVariant(event) {
    event.preventDefault();
  }

  function onDropVariant(event) {
    const extraIdToMove = event.dataTransfer.getData('text');
    sortVariants(extraIdToMove, event.target.id);
    event.dataTransfer.clearData();
  }

  return (
    <Modal onClose={onClose}>
      <Sheet>
        <Field>
          <pre>{product.id}</pre>
        </Field>
        <ProductImage src={values.image} />
        <Field>
          <input type="file" onChange={onUploadImage} />
          <div>
            {categoryPlaceholderList.map((placeholderUrl) => (
              <PlaceholderSelectionImg
                src={placeholderUrl}
                alt={placeholderUrl}
                selected={placeholderUrl === values.image}
                onClick={() => setValues({ ...values, image: placeholderUrl })}
              />
            ))}
          </div>
          <Button small onClick={removeImage} loading={removingProductImage}>
            {i18n.t('Menu:removeImage')}
          </Button>
        </Field>
        <Field>
          <TextInput
            placeholder={i18n.t('Menu:productName')}
            name="name"
            value={values.name}
            onChange={handleChange}
          />
        </Field>
        <Field>
          <TextInput
            placeholder={`${i18n.t('Menu:productPrice')}: ${formatPrice(
              values.price,
            )}`}
            name="price"
            value={values.price}
            onChange={handleChange}
            type="number"
          />
        </Field>
        <Field>
          <TextInput
            placeholder={`${i18n.t('Menu:oldPrice')}: ${formatPrice(
              values.old_price,
            )}`}
            name="old_price"
            value={values.old_price}
            onChange={handleChange}
            type="number"
            disabled={!!values.variants}
          />
        </Field>
        <Field>
          <TextInput
            placeholder={i18n.t('Menu:teaserText')}
            name="teaser_text"
            multiline
            rows={3}
            value={values.teaser_text || ''}
            onChange={handleChange}
          />
        </Field>
        <Field>
          <TextInput
            placeholder={i18n.t('Menu:teaserTextL')}
            name="teaser_text_long"
            multiline
            rows={5}
            value={values.teaser_text_long || null}
            onChange={handleChange}
          />
        </Field>

        <HeaderSmall>{i18n.t('Menu:productToggles')}</HeaderSmall>
        <Field>
          <CheckBoxLabelContainer
            onClick={() => handleChangeManually(!values.published, 'published')}
          >
            <Checkbox checked={values.published}></Checkbox>
            <CheckBoxLabel>{i18n.t('Menu:published')}</CheckBoxLabel>
          </CheckBoxLabelContainer>
        </Field>
        <Field>
          <TextInput
            placeholder={i18n.t('Menu:contingent')}
            name="contingent"
            value={values.contingent || 0}
            onChange={handleChange}
            type="number"
          />
        </Field>

        <VatSelector
          vatRates={vatRates}
          vatRate={values.vat_rate}
          onChange={handleChange}
        />

        <Field>
          <HeaderSmall>{i18n.t('Menu:hasCutlery')}</HeaderSmall>
          <Select
            name="has_cutlery"
            defaultValue={values.has_cutlery}
            onChange={handleChange}
          >
            <option value="null">-</option>
            <option value>{i18n.t('Common:yes')}</option>
            <option value={false}>{i18n.t('Common:no')}</option>
          </Select>
        </Field>

        <ExtraGroupFormSection values={values} setValues={setValues} />

        {values.variants && (
          <Field>
            <HeaderSmall>{i18n.t('Menu:changeVariantsOrder')}</HeaderSmall>
            {(values.variants || []).map((variant) => (
              <Draggable
                key={variant.variant_id}
                id={variant.variant_id}
                onDragStart={onDragStartVariant}
                onDragOver={onDragOverVariant}
                onDrop={onDropVariant}
                draggable
              >
                {`${variant.name} - ${variant.title} - ${variant.price}`}
              </Draggable>
            ))}
          </Field>
        )}

        {usedMultipleTimes && (
          <CategoryLinkContainer>
            {i18n.t('Menu:productUsedIn')}: {storeCatLinks}
          </CategoryLinkContainer>
        )}
        <ProductButtonContainer>
          <Button
            onClick={handleSave}
            loading={updating}
            disabled={!Object.keys(touchedValues).length}
          >
            {usedMultipleTimes
              ? `${i18n.t('Menu:save')} (${inCategoriesUsedCount})`
              : i18n.t('Menu:save')}
          </Button>
          {usedMultipleTimes && (
            <SaveCopyButton
              disable={!Object.keys(touchedValues).length}
              loading={updating}
              onClick={handleSaveAsCopy}
            >
              {i18n.t('Menu:saveCopy')}
            </SaveCopyButton>
          )}

          {/* // TODO
          <RemoveDeleteButtonContainer>
            <RemoveFromCatButton onClick={handleRemoveFromCategory} disable={updating} loading={updating}>Remove from Category</RemoveFromCatButton>
            {!usedMultipleTimes && <DeleteProductButton onClick={handleRemoveAndDeleteProduct} disable={updating} loading={updating}>Delete Product</DeleteProductButton>}
          </RemoveDeleteButtonContainer>
           */}
        </ProductButtonContainer>
      </Sheet>
    </Modal>
  );
}

ProductEditModal.propTypes = {
  product: PropTypes.object.isRequired,
  storeId: PropTypes.string.isRequired,
  category: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  productCategoryMap: PropTypes.object.isRequired,
  vatRates: PropTypes.arrayOf(PropTypes.number).isRequired,
};

export default ProductEditModal;
